import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { setModal, selectModal } from "../../redux/modalSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRightFromBracket, faClose, faSearch, faTrash } from "@fortawesome/free-solid-svg-icons";

import { useRequest } from "../../hooks/UseRequest";
import Pagination from "../../components/Pagination";
import { setNotification } from "../../redux/notificationSlice";
import { selectDropdowns, selectDropdownOptions } from '../../redux/dropdownSlice'
import { selectUser } from '../../redux/userSlice'
import Select from 'react-select'
import { reactSelectClassNames } from '../../helpers/react-select-styles'

export default function CustomerSearchCheckEntryModal() {
  const requests = useRequest()
  const dispatch = useDispatch();
  const modal = useSelector(selectModal);
  const user = useSelector(selectUser);
  const [searchKeyWords, setSearchKeyWords] = useState('');
  const [meta, setMeta] = useState()
  const [loading, setLoading] = useState(false)
  const [stateId, setStateId] = useState('')

  const dropdowns = useSelector(selectDropdowns)
  const dropdownOptions = useSelector(selectDropdownOptions)

  const state = dropdowns.data.find((o) => o.code_key === 'state')
  const stateOptions = dropdownOptions.filter((o) => o.dropdown_id === state?.id).map((o) => ({ label: o.name, value: o.id, codeKey: o.code_key }))

  const [searchResults, setSearchResults] = useState(null);
  const [lastFilters, setLastFilters] = useState(null);

  const active = modal.is_active && modal.type === 'customer_search_check_entry_modal'
  const searchRequestId = useRef(0)

  const resetState = () => {
    // Invalidate any search still in flight so its results can't land on the next opening.
    searchRequestId.current += 1
    setSearchResults(null)
    setSearchKeyWords('')
    setStateId('')
    setMeta()
    setLastFilters(null)
    setLoading(false)
  }

  // This modal stays mounted in the Footer, so the previous check's search (including a state
  // filter no longer visible in the Select) would otherwise still apply to the next one.
  useEffect(() => {
    if (active) resetState()
  }, [active, modal.options?.portfolio_id])

  const handleCancel = () => {
    resetState()
    dispatch(setModal({ is_active: false }));
  };

  const handleInputChange = (x) => {
    x?.preventDefault()
    const searchKeyWords = x?.target?.value
    setSearchKeyWords(searchKeyWords)
  }

  const handleSearch = async (x) => {
    const searchQuery = {
      and: [{
        or: [
          { column: "guid", value: `${searchKeyWords}`, operator: "like" },
          { column: "ssn_last_four", value: `${searchKeyWords}`, operator: "like" },
          { column: "address", value: `${searchKeyWords}`, operator: "like" },
          { column: "address2", value: `${searchKeyWords}`, operator: "like" },
          { column: "first_name", value: `${searchKeyWords}`, operator: "like" },
          { column: "last_name", value: `${searchKeyWords}`, operator: "like" },
          { column: "city", value: `${searchKeyWords}`, operator: "like" },
          { column: "zip_code_label", value: `${searchKeyWords}`, operator: "like" },
        ]
      },
      { column: "portfolio_id", value: modal.options.portfolio_id, operator: "=" },
      ]
    };

    if (stateId) {
      searchQuery.and.push({
        column: "state_dropdown_option_id",
        value: `${stateId}`,
        operator: "="
      });
    }

    setLastFilters(searchQuery)
    await handleSearchRequest({ filters: JSON.stringify(searchQuery), per_page: 5 })
  }

  const handleKeyDown = (event) => {
    if (event?.key === 'Enter') handleSearch();
  }

  const handleSearchRequest = async (body) => {
    const requestId = ++searchRequestId.current
    setLoading(true)
    try {
      // This search matches on ssn_last_four, so it uses POST instead of GET to keep the
      // value out of the URL. useRequest's post() doesn't append spoofUserID like get()
      // does, so it's added manually here: the backend reads it from the query string,
      // not the JSON body.
      const spoofParam = user.spoofed_user?.id ? `?spoofUserID=${user.spoofed_user.id}` : ''
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/search${spoofParam}`)
      const results = await requests.post(body);
      if (requestId !== searchRequestId.current) return
      setSearchResults(results.data.data);
      setMeta(results.data.meta);
    } catch (error) {
      if (requestId !== searchRequestId.current) return
      dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
    }
    setLoading(false)
  }

  // Pagination hands back a GET-style URL (e.g. "...?page=2"). Pull the page number out
  // of it and re-run the search via POST so filters stay out of the query string.
  const handlePaginate = async (url) => {
    const page = url?.match(/page=(\d+)/)?.[1] ?? 1
    await handleSearchRequest({ filters: JSON.stringify(lastFilters), per_page: 5, page })
  }

  const handleSelectCustomer = (customer) => {
    modal.options.successCallback({ name: `${customer.first_name} ${customer.last_name}`, ...customer });
    handleCancel();
  }

  const searchRef = useRef(null);

  // eslint-disable-next-line no-nested-ternary
  return modal.is_active && modal.type === 'customer_search_check_entry_modal' ? (
    // eslint-disable-next-line no-nested-ternary
    <div className="modal is-active">
      <div className="modal-background"></div>
      <div className="modal-card is-7" style={{ width: "70%" }}>
        <header className="modal-card-head py-3">
          <p className="modal-card-title is-capitalized">Customer Search</p>
          <button className="delete" aria-label="close" onClick={handleCancel}></button>
        </header>
        <section
          className="modal-card-body modal-card-body mb-0 pt-1 pb-1"
          style={{ minHeight: searchResults ? "400px" : "110px" }}
        >
          <div className="columns is-centered is-vcentered is-fullheight" style={{ height: "100%", maxHeight: "110px", width: "95%" }}>
            <div
              className={`column is-four-fifths is-flex is-justify-content-center`}
            >
              <div className="control has-icons-left has-icons-right is-fullwidth" style={{ width: '100%' }}>
                <input
                  className="input"
                  value={searchKeyWords || ''}
                  type="text"
                  /* eslint-disable-next-line jsx-a11y/no-autofocus */
                  autoFocus
                  onKeyDown={handleKeyDown}
                  placeholder="Filter By Customer #, SSN, First Name, Last Name, Address, City, Zip"
                  onChange={handleInputChange}
                  ref={searchRef}
                />
                <span className="icon is-small is-left">
                  <FontAwesomeIcon icon={faSearch} />
                </span>
                {searchKeyWords && (
                  <span
                    className="icon is-small is-right is-clickable"
                    onClick={() => setSearchKeyWords('')}
                  >
                    <FontAwesomeIcon icon={faClose} />
                  </span>
                )}
              </div>

              <div className="control has-icons-left has-icons-right is-fullwidth ml-5" style={{ width: '40%' }}>
                <Select
                  options={stateOptions}
                  value={stateOptions.find((o) => o.value === stateId) ?? null}
                  isSearchable
                  onChange={(val) => {
                    setStateId(val?.value ?? '')
                  }}
                  menuPosition="fixed"
                  styles={{ menu: ({ ...css }) => ({ ...css, width: '100%' }) }}
                  isClearable
                  placeholder="Select State"
                />
              </div>

            </div>
            <div className="column  is-flex is-justify-content-center">
              <button
                disabled={searchKeyWords == null || loading}
                className="button is-link has-text-white header-button px-6"
                onClick={handleSearch}
              >Search
              </button>
            </div>
          </div>
          {searchResults ? (
            <div className="columns my-2">
              <div
                className="lox-hooktable column is-full table-container m-0"
              >
                <table
                  className="lox-hooktable-table table is-bordered is-striped is-narrow is-hoverable is-fullwidth is-grey"
                >
                  <thead>
                    <tr className="is-selected">
                      <th className="is-selected has-text-centered">Select</th>
                      <th className="is-selected has-text-centered">First</th>
                      <th className="is-selected has-text-centered">Last</th>
                      <th className="is-selected has-text-centered">Street</th>
                      <th className="is-selected has-text-centered">City</th>
                      <th className="is-selected has-text-centered">State</th>
                      <th className="is-selected has-text-centered">Customer #</th>
                      <th className="is-selected has-text-centered">Last 4 of SSN</th>
                      <th className="is-selected has-text-centered">Zip Code</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchResults.map((row, index) => {
                      return (
                        <tr key={row.guid}>
                          <td className="has-text-left"><a onClick={() => { handleSelectCustomer(row) }} className="is-flex is-primary is-vcentered is-align-items-center is-justify-content-center">
                            <FontAwesomeIcon icon={faArrowRightFromBracket} />
                          </a></td>
                          <td className="has-text-left">{row.first_name}</td>
                          <td className="has-text-left">{row.last_name}</td>
                          <td className="has-text-left">{row.address}</td>
                          <td className="has-text-left">{row.city}</td>
                          <td className="has-text-left">{stateOptions.find((o) => o.value == row.state_dropdown_option_id)?.label}</td>
                          <td className="has-text-left">{row.id}</td>
                          <td className="has-text-left">{row.ssn_last_four}</td>
                          <td className="has-text-left">{row.zip_code_label}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
                {meta ? <Pagination meta={meta} paginateCallback={handlePaginate} /> : null}
              </div>
            </div>
          ) : null}
        </section>
        <footer className="modal-card-foot is-justify-content-end py-1">
          <div className="portfolio-select-header"></div>
          <div className="lox-form__buttons-wrapper has-text-right">
          </div>
        </footer>
      </div>
    </div>
  ) : null;
}
